import {
  Stack,
  TextField,
  Typography,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Link,
  Alert,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { useFormikContext } from "formik";

const USE_CASE_MESSAGE = {
  home: "Perfect! We'll set up a free account for your home charging needs.",
  business: "Great! Let's set up your business or fleet charging account.",
};

export default function YourNeedsStep({ onBack, onSubmit, submitLabel = "Confirm Your Email" }) {
  const { values, errors, touched, isSubmitting, status, getFieldProps, setFieldValue } =
    useFormikContext();

  const isBusiness = values.useCase === "business";
  const message = USE_CASE_MESSAGE[values.useCase] ?? USE_CASE_MESSAGE.home;

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6" fontWeight={600}>
          Your Needs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          What do you need?
        </Typography>
      </Box>

      {status && <Alert severity="error">{status}</Alert>}

      {isBusiness && (
        <TextField
          fullWidth
          required
          label="Company Name"
          placeholder="Acme Inc."
          {...getFieldProps("companyName")}
          error={Boolean(touched.companyName && errors.companyName)}
          helperText={touched.companyName && errors.companyName}
        />
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 2,
          borderRadius: "10px",
          bgcolor: "action.hover",
        }}
      >
        <HomeOutlinedIcon color="primary" />
        <Typography variant="body2">{message}</Typography>
      </Box>

      {isBusiness && (
        <TextField
          fullWidth
          type="number"
          label="How many chargers do you plan to manage?"
          placeholder="e.g., 5"
          inputProps={{ min: 1, step: 1 }}
          {...getFieldProps("chargerCount")}
          error={Boolean(touched.chargerCount && errors.chargerCount)}
          helperText={touched.chargerCount && errors.chargerCount}
        />
      )}

      <TextField
        fullWidth
        multiline
        rows={3}
        label={isBusiness ? "Description" : "Tell us about your charging setup (Optional)"}
        placeholder="e.g., Single charger in garage for daily use"
        {...getFieldProps("chargingSetup")}
        error={Boolean(touched.chargingSetup && errors.chargingSetup)}
        helperText={touched.chargingSetup && errors.chargingSetup}
      />

      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={values.acceptedTerms}
              onChange={(e) => setFieldValue("acceptedTerms", e.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              I agree to the{" "}
              <Link href="/terms-of-service" target="_blank" rel="noopener">
                Terms of Service
              </Link>
              ,{" "}
              <Link href="/acceptable-use-policy" target="_blank" rel="noopener">
                Acceptable Use Policy
              </Link>
              , and{" "}
              <Link href="/privacy-policy" target="_blank" rel="noopener">
                Privacy Policy
              </Link>
              .
            </Typography>
          }
        />
        {touched.acceptedTerms && errors.acceptedTerms && (
          <FormHelperText error>{errors.acceptedTerms}</FormHelperText>
        )}
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button onClick={onBack} startIcon={<span aria-hidden>←</span>}>
          Back
        </Button>
        <LoadingButton variant="contained" loading={isSubmitting} onClick={onSubmit}>
          {submitLabel}
        </LoadingButton>
      </Stack>
    </Stack>
  );
}